import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Paper,
  Chip,
  Stack,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { toast } from 'sonner';
import axios from 'axios';
import Constants from '../../config/constants';
import { REVIEW_KEYWORDS, ReviewKeyword } from '../../constants/reviewKeywords';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  PhotoCamera,
  Send,
  Star,
  Close,
  CheckCircle,
} from '@mui/icons-material';

interface FormData {
  customerName: string;
  customerPhone: string;
  menu: string;
  reviewText: string;
  selectedKeywords: string[];
  beforeImageFile?: File;
  afterImageFile?: File;
  privacyAgreed: boolean;
}

const MAX_KEYWORDS_PREVIEW = 5;

const ReviewWrite: React.FC = () => {
  const navigate = useNavigate();
  const { branchCode } = useParams<{ branchCode: string }>();
  const [loading, setLoading] = useState(false);
  const [actualBranchId, setActualBranchId] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    customerPhone: '',
    menu: '',
    reviewText: '',
    selectedKeywords: [],
    beforeImageFile: undefined,
    afterImageFile: undefined,
    privacyAgreed: false,
  });
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [beforeImagePreview, setBeforeImagePreview] = useState<string>('');
  const [afterImagePreview, setAfterImagePreview] = useState<string>('');

  // QR코드 파라미터에서 실제 branchId를 추출하는 함수
  const extractBranchId = async (qrParam: string): Promise<number> => {
    if (qrParam.startsWith('BRANCH_')) {
      // 기존 BRANCH_ 형식
      return parseInt(qrParam.replace('BRANCH_', ''));
    } else if (qrParam.startsWith('OWNER_')) {
      // 새로운 OWNER_ 형식 - owner의 userid로 branch 조회
      const ownerUserId = qrParam.replace('OWNER_', '');
      try {
        const response = await axios.get(
          `${Constants.API_HOST}/api/app/branch/my-branches`,
          {
            params: { ownerUserId: ownerUserId },
          },
        );

        if (response.data.success && response.data.data.length > 0) {
          // 첫 번째 branch의 ID를 사용
          return response.data.data[0].id;
        } else {
          toast.error('해당 Owner의 지점 정보를 찾을 수 없습니다.');
          return 1; // 기본값
        }
      } catch (error) {
        console.error('Owner의 지점 정보 조회 오류:', error);
        toast.error('지점 정보를 가져오는데 실패했습니다.');
        return 1; // 기본값
      }
    } else {
      // 기본값 또는 숫자만 있는 경우
      return parseInt(qrParam) || 1;
    }
  };

  useEffect(() => {
    const initializeBranchId = async () => {
      if (branchCode) {
        const extractedBranchId = await extractBranchId(branchCode);
        setActualBranchId(extractedBranchId);
      }
    };

    initializeBranchId();
  }, [branchCode]);

  const handleKeywordToggle = (keywordId: string) => {
    const current = formData.selectedKeywords;
    let next: string[];
    if (current.includes(keywordId)) {
      next = current.filter((id) => id !== keywordId);
    } else {
      if (current.length >= 5) {
        toast.error('키워드는 최대 5개까지 선택할 수 있습니다.');
        return;
      }
      next = [...current, keywordId];
    }
    setFormData({ ...formData, selectedKeywords: next });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'before' | 'after',
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 크기 검증 (10MB 제한)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('이미지 파일은 10MB 이하로 선택해주세요.');
      return;
    }

    // 이미지 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 선택해주세요.');
      return;
    }

    setFormData((prev) => ({ ...prev, [`${type}ImageFile`]: file }));

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = e.target?.result as string;
      if (type === 'before') {
        setBeforeImagePreview(preview);
      } else {
        setAfterImagePreview(preview);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageDelete = (type: 'before' | 'after') => {
    setFormData((prev) => ({ ...prev, [`${type}ImageFile`]: undefined }));
    if (type === 'before') {
      setBeforeImagePreview('');
    } else {
      setAfterImagePreview('');
    }
  };

  const handleExpandToggle = (cat: string) => {
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const serviceType = '일반';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.reviewText.trim()) {
      toast.error('리뷰 내용을 입력해주세요.');
      return;
    }
    if (!formData.selectedKeywords.length) {
      toast.error('좋았던 점을 1개 이상 선택해주세요.');
      return;
    }
    if (!formData.beforeImageFile || !formData.afterImageFile) {
      toast.error('식전/식후 사진을 모두 업로드해주세요.');
      return;
    }
    // 개인정보가 입력된 경우 동의 체크 확인
    if ((formData.customerName.trim() || formData.customerPhone.trim()) && !formData.privacyAgreed) {
      toast.error('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }
    try {
      setLoading(true);
      const data = new FormData();
      data.append('branchId', String(actualBranchId));
      data.append('serviceType', serviceType);
      data.append('menu', formData.menu);
      data.append('reviewText', formData.reviewText);
      data.append('positiveOptions', JSON.stringify(formData.selectedKeywords));
      data.append('beforeImage', formData.beforeImageFile);
      data.append('afterImage', formData.afterImageFile);

      // 선택사항 필드들
      data.append('customerName', formData.customerName);
      data.append('customerPhone', formData.customerPhone);

      await axios.post(`${Constants.API_HOST}/api/app/review`, data);
      navigate('/review/success', { state: { branchId: actualBranchId } });
    } catch (error: any) {
      console.error('리뷰 등록 오류:', error);
      const errorMessage =
        error.response?.data?.message || '리뷰 등록에 실패했습니다.';
      toast.error(errorMessage);
      if (error.response?.data?.field) {
        toast.error(
          `서버에서 예상하지 못한 필드: ${error.response.data.field}`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const categories = ['음식/가격', '서비스', '분위기', '기타'] as const;

  return (
    <Container maxWidth='sm' sx={{ mt: 2, mb: 2, px: { xs: 0.5, sm: 2 } }}>
      {/* 헤더 */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 3, bgcolor: '#42424a', color: 'white' }}
      >
        <Typography variant='h5' textAlign='center' fontWeight='bold'>
          ⭐ 리뷰 작성하기 ⭐
        </Typography>
        <Typography
          variant='body2'
          textAlign='center'
          sx={{ mt: 1, opacity: 0.9 }}
        >
          맛있게 드신 음식에 대한 후기를 남겨주세요
        </Typography>
      </Paper>

      <Card sx={{ boxShadow: 2, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* 고객 정보 (선택사항) */}
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Star color='warning' fontSize='small' />
                  고객 정보 (선택사항)
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  입력하시면 더 나은 음식과 서비스 제공에 도움이 됩니다
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label='이름'
                      name='customerName'
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder='선택사항'
                      variant='outlined'
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '50px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '16px',
                          padding: '8px 12px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '15px',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label='전화번호'
                      name='customerPhone'
                      value={formData.customerPhone}
                      onChange={handleChange}
                      placeholder='010-0000-0000'
                      variant='outlined'
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '50px',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '16px',
                          padding: '14px 16px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '15px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {/* 개인정보 처리 동의 */}
                {(formData.customerName.trim() || formData.customerPhone.trim()) && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.privacyAgreed}
                          onChange={(e) => setFormData({ ...formData, privacyAgreed: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          개인정보 수집 및 이용에 동의합니다 (필수)
                        </Typography>
                      }
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1, fontSize: '13px' }}>
                        📋 개인정보 수집 및 이용 안내
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px', lineHeight: 1.4 }}>
                        <strong>• 수집목적:</strong> 리뷰 관리, 고객 서비스 개선, 맞춤형 서비스 제공
                        <br />
                        <strong>• 수집항목:</strong> 이름, 전화번호 (선택사항)
                        <br />
                        <strong>• 보유기간:</strong> 리뷰 삭제 시까지 또는 수집일로부터 3년
                        <br />
                        <strong>• 제3자 제공:</strong> 원칙적으로 제공하지 않음 (법령 요구 시 예외)
                        <br />
                        <strong>• 정보주체 권리:</strong> 열람, 정정·삭제, 처리정지 요구 가능
                        <br />
                        <strong>• 동의 거부:</strong> 거부 시 개인정보 관련 서비스 이용 제한 가능
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* 메뉴 정보 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='어떤 메뉴를 드셨나요?'
                  name='menu'
                  value={formData.menu}
                  onChange={handleChange}
                  placeholder='예) 김치찌개, 삼겹살, 파스타, 피자 등'
                  required
                  sx={{
                    mb: 1,
                    background: '#fff',
                    borderRadius: 2,
                    '& .MuiInputBase-root': {
                      height: '50px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      padding: '14px 16px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '15px',
                    },
                  }}
                />
              </Grid>

              {/* 리뷰 내용 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label='리뷰 내용 *'
                  name='reviewText'
                  value={formData.reviewText}
                  onChange={handleChange}
                  placeholder='음식, 분위기, 서비스 등 자유롭게 작성해 주세요.'
                  required
                  sx={{
                    mb: 1,
                    background: '#fff',
                    borderRadius: 2,
                    '& .MuiInputBase-root': {
                      minHeight: '120px',
                      whiteSpace: 'pre-wrap',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      padding: '14px 16px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '15px',
                    },
                    '& textarea': {
                      whiteSpace: 'pre-wrap',
                    },
                  }}
                />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  textAlign='right'
                >
                  {formData.reviewText.length} / 1000자
                </Typography>
              </Grid>

              {/* 식전/식후 사진 */}
              <Grid item xs={12}>
                <Typography
                  variant='h6'
                  gutterBottom
                  sx={{ color: 'error.main' }}
                >
                  식전/식후 사진 * (필수)
                </Typography>
                <Grid container spacing={2}>
                  {/* 식전 사진 */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      식전 사진
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        minHeight: 150,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        bgcolor: beforeImagePreview ? 'transparent' : 'grey.50',
                      }}
                    >
                      {beforeImagePreview ? (
                        <>
                          <Box
                            component='img'
                            src={beforeImagePreview}
                            alt='식전 사진'
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 200,
                              objectFit: 'contain',
                              borderRadius: 1,
                            }}
                          />
                          <IconButton
                            size='small'
                            onClick={() => handleImageDelete('before')}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' },
                            }}
                          >
                            <Close fontSize='small' />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <PhotoCamera
                            sx={{
                              fontSize: 40,
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          />
                          <Typography variant='body2' color='text.secondary'>
                            식전 사진 선택
                          </Typography>
                        </>
                      )}
                      <input
                        type='file'
                        accept='image/*'
                        onChange={(e) => handleImageChange(e, 'before')}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* 식후 사진 */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      식후 사진
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        minHeight: 150,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        bgcolor: afterImagePreview ? 'transparent' : 'grey.50',
                      }}
                    >
                      {afterImagePreview ? (
                        <>
                          <Box
                            component='img'
                            src={afterImagePreview}
                            alt='식후 사진'
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 200,
                              objectFit: 'contain',
                              borderRadius: 1,
                            }}
                          />
                          <IconButton
                            size='small'
                            onClick={() => handleImageDelete('after')}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' },
                            }}
                          >
                            <Close fontSize='small' />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <PhotoCamera
                            sx={{
                              fontSize: 40,
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          />
                          <Typography variant='body2' color='text.secondary'>
                            식후 사진 선택
                          </Typography>
                        </>
                      )}
                      <input
                        type='file'
                        accept='image/*'
                        onChange={(e) => handleImageChange(e, 'after')}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* 좋았던 점 선택 */}
              <Grid item xs={12}>
                <Typography variant='h6' gutterBottom>
                  만족하신 부분을 선택해주세요 *
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 2 }}
                >
                  해당하는 항목을 선택해주세요 (최대 5개)
                </Typography>

                {categories.map((category) => {
                  const categoryKeywords = REVIEW_KEYWORDS.filter(
                    (keyword) => keyword.category === category,
                  );
                  const isExpanded = expanded[category];
                  const displayKeywords = isExpanded
                    ? categoryKeywords
                    : categoryKeywords.slice(0, MAX_KEYWORDS_PREVIEW);

                  return (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography
                        variant='subtitle2'
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {category}
                      </Typography>
                      <Stack direction='row' flexWrap='wrap' gap={1}>
                        {displayKeywords.map((keyword: ReviewKeyword) => (
                          <Chip
                            key={keyword.id}
                            label={keyword.label}
                            onClick={() => handleKeywordToggle(keyword.id)}
                            variant={
                              formData.selectedKeywords.includes(keyword.id)
                                ? 'filled'
                                : 'outlined'
                            }
                            color={
                              formData.selectedKeywords.includes(keyword.id)
                                ? 'primary'
                                : 'default'
                            }
                            size='small'
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                        {categoryKeywords.length > MAX_KEYWORDS_PREVIEW && (
                          <Chip
                            label={isExpanded ? '접기' : '더보기'}
                            onClick={() => handleExpandToggle(category)}
                            variant='outlined'
                            size='small'
                            icon={
                              isExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            }
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Grid>

              {/* 제출 버튼 */}
              <Grid item xs={12}>
                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  fullWidth
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <Send />
                  }
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background:
                      'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    '&:hover': {
                      background:
                        'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                    },
                  }}
                  style={{
                    color: 'white',
                  }}
                >
                  {loading ? '등록 중...' : '리뷰 등록하기'}
                </Button>
              </Grid>
            </Grid>
          </form>

          {/* 안내 문구 */}
          <Alert severity='info' sx={{ mt: 3 }}>
            <Typography variant='body2'>
              • 이름과 전화번호는 선택사항입니다
              <br />
              • Before/After 사진과 리뷰 내용은 필수입니다
              <br />
              • 등록된 리뷰는 매장 홍보에 활용될 수 있습니다
              <br />
              • 개인정보 입력 시 개인정보 처리방침에 동의해야 합니다
            </Typography>
          </Alert>
        </CardContent>
      </Card>

    </Container>
  );
};

export default ReviewWrite;
